import {Button, Caption, Cell, Switch, Textarea} from "@telegram-apps/telegram-ui";
import {ChangeEvent, useEffect, useState} from "react";
import {TbCalendar, TbSparkles} from "react-icons/tb";
import {AuthProfile, Wish} from "@/api/types.ts";
import {markWishGifted} from "@/api/apiMethods.ts";
import {useNavigation} from "@/providers/NavigationProvider.tsx";
import {useAuthProfile} from "@/providers/AuthProfileProvider.tsx";
import {haptic} from "@/helpers/haptic.ts";
import {mainButton} from "@telegram-apps/sdk-react";
import {setOnClickMainButton} from "@/helpers/tgButtons.ts";
import {useTranslation} from "react-i18next";
import {ModalDatePicker} from "@/components/ModalDatePicker/ModalDatePicker.tsx";
import "./styles.css";

const parseGiftedDate = (date: Date | string | null | undefined) => {
  if (!date) {
    return null;
  }

  const parsedDate = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }

  return new Date(parsedDate.getUTCFullYear(), parsedDate.getUTCMonth(), parsedDate.getUTCDate());
};

const buildUtcDate = (date: Date) => {
  const utcDate = new Date();
  utcDate.setUTCFullYear(date.getFullYear());
  utcDate.setUTCMonth(date.getMonth());
  utcDate.setUTCDate(date.getDate());
  utcDate.setUTCHours(0, 0, 0, 0);
  return utcDate;
};

export const GiftedWishForm = (
  {wish, onSaved, showGiftedBy, onShowGiftedByChange, onGiftedCommentChange, onClose}:
  {
    wish: Wish,
    onSaved: (wish: Wish) => void,
    showGiftedBy: boolean,
    onShowGiftedByChange: (showGiftedBy: boolean) => void,
    onGiftedCommentChange?: (giftedComment: string) => void,
    onClose?: () => void,
  }
) => {
  const [giftedAt, setGiftedAt] = useState<Date>(parseGiftedDate(wish.giftedAt) || new Date());
  const [giftedComment, setGiftedComment] = useState<string>(wish.giftedComment || "");
  const [isSaveInProgress, setIsSaveInProgress] = useState<boolean>(false);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState<boolean>(false);
  const {showSnackbar} = useNavigation();
  const {t, i18n: {language}} = useTranslation();

  let authProfileFromContext: AuthProfile | undefined = undefined;
  try {
    const {authProfile} = useAuthProfile();
    authProfileFromContext = authProfile;
  } catch (e) { /* empty */ }

  const giftedByUser = wish.bookedByUser;
  const canShowGiftedBy = Boolean(wish.bookedBy && giftedByUser);
  const isGiftedByCurrentUser = giftedByUser?.id === authProfileFromContext?.user.id;
  const shouldConfirmGiftedBy = authProfileFromContext?.user.showBookings !== "public" && !isGiftedByCurrentUser;

  const handleShowGiftedByChange = (event: ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    haptic.selection();

    if (checked && shouldConfirmGiftedBy) {
      const isConfirmed = confirm(t("wish.gifted.confirmShowGiftedBy"));
      if (!isConfirmed) {
        return;
      }
    }

    onShowGiftedByChange(checked);
  };

  const saveGiftedWish = async () => {
    setIsSaveInProgress(true);
    try {
      const updatedWish = await markWishGifted(wish.id, {
        giftedAt: buildUtcDate(giftedAt),
        giftedComment: giftedComment || null,
        showGiftedBy: canShowGiftedBy && showGiftedBy,
      });
      haptic.notification("success");
      showSnackbar("✅", "system.wishGifted");
      onSaved(updatedWish);
      onClose?.();
    } catch (e) {
      haptic.notification("error");
      showSnackbar("❌", "system.unknowError");
    }
    setIsSaveInProgress(false);
  };

  useEffect(() => {
    if (isDatePickerOpen) {
      return;
    }
    setOnClickMainButton(async () => {
      await saveGiftedWish();
    });
    mainButton.setParams({
      isVisible: true,
      isLoaderVisible: isSaveInProgress,
      isEnabled: !isSaveInProgress,
      text: t("action.save"),
    });
  }, [isDatePickerOpen, isSaveInProgress, saveGiftedWish, t]);

  const openDatePicker = () => {
    haptic.impact("light");
    setIsDatePickerOpen(true);
  };

  const onGiftedDateSaved = async (date: Date) => {
    setGiftedAt(date);
    setIsDatePickerOpen(false);
  };

  const onGiftedDateCancel = () => {
    setIsDatePickerOpen(false);
  };

  const handleGiftedCommentChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setGiftedComment(value);
    onGiftedCommentChange?.(value);
  };

  const showGiftedByDescription = [
    !isGiftedByCurrentUser ? t("wish.gifted.showGiftedByNotificationHint") : undefined,
    showGiftedBy ? t("wish.gifted.showGiftedByHint") : undefined,
  ].filter(Boolean).join(" ") || undefined;

  return (
    <div className="gifted-wish-form safe-area-bottom">
      <Cell
        before={<TbCalendar size={20}/>}
        after={<Caption weight="2" className="tg-link-color">{giftedAt.toLocaleDateString(language, {year: "numeric", month: "long", day: "numeric"})}</Caption>}
        onClick={openDatePicker}
      >
        {t("wish.gifted.date")}
      </Cell>

      <Textarea
        placeholder={t("wish.gifted.comment")}
        value={giftedComment}
        maxLength={255}
        onChange={handleGiftedCommentChange}
      />

      {canShowGiftedBy && (
        <Cell
          Component="label"
          multiline={true}
          description={showGiftedByDescription}
          after={<Switch checked={showGiftedBy} onChange={handleShowGiftedByChange}/>}
        >
          {t("wish.gifted.showGiftedBy")}
        </Cell>
      )}

      {import.meta.env.MODE === "development" && (
        <Button
          stretched={true}
          before={<TbSparkles size={20}/>}
          loading={isSaveInProgress}
          disabled={isSaveInProgress}
          onClick={saveGiftedWish}
        >
          {t("action.save")}
        </Button>
      )}

      {isDatePickerOpen && (
        <ModalDatePicker
          opened={isDatePickerOpen}
          initialDate={giftedAt}
          onApply={onGiftedDateSaved}
          onCancel={onGiftedDateCancel}
          title={t("wish.gifted.date")}
        />
      )}
    </div>
  );
};
